import { v3, quat } from "oc-threejs/short3";
// @ts-ignore
import lightGlowVertShader from "./shaders/lightGlow.vert.glsl?raw";
// @ts-ignore
import lightGlowFragShader from "./shaders/lightGlow.frag.glsl?raw";
// @ts-ignore
import lightGlowDownFragShader from "./shaders/lightGlowDown.frag.glsl?raw";

import { type FakeLightPlaneProps, fakeLightDefaultProps } from "../nodes/metadata/ModelLightJson";
export { type FakeLightPlaneProps, fakeLightDefaultProps };
import * as Maths3 from "oc-threejs/Maths3";
import {
  Box3,
  Color,
  DoubleSide,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Vector3,
  Quaternion,
} from "three";

export function createFakeLightPlanes(box3: Box3, options: FakeLightPlaneProps) {
  const {
    name,
    type,
    intensity,
    fakeLightColor,
    falloffIntensity,
    edgeMargin,
    fuzziness,
    seiteVorne,
    seiteHinten,
    seiteLinks,
    seiteRechts,
    seiteUnten,
    neigung,
    sizeOffset,
    centerOffset,
    visible,
  } = {
    ...fakeLightDefaultProps,
    ...options,
  };

  const createFakeLightPlane = (width: number, height: number) => {
    const fragmentShader = type == "GlowDown" ? lightGlowDownFragShader : lightGlowFragShader;

    const glowMaterial = new ShaderMaterial({
      transparent: true,
      side: DoubleSide,
      depthWrite: false, // don’t block other glow
      uniforms: {
        color: { value: new Color(fakeLightColor) },
        intensity: { value: intensity },
        falloffIntensity: { value: falloffIntensity }, // how wide the glow edge is
        edgeMargin: { value: edgeMargin }, // how far from the edge the glow starts
        fuzziness: { value: fuzziness }, // noise amount, stay below 0.05
        halfSize: { value: v3(width / 2, height / 2) },
      },
      vertexShader: lightGlowVertShader,
      fragmentShader,
    });

    const geometry = new PlaneGeometry(width, height, 1, 1);
    const glowPlane = new Mesh(geometry, glowMaterial);
    glowPlane.name = options.name;
    (glowPlane as any).isFakeLight = true;
    return glowPlane;
  };

  const b = box3.clone();
  b.expandByVector(v3().fromArray(sizeOffset));
  b.translate(v3().fromArray(centerOffset));
  const boxSize = b.getSize(v3());

  const seiten = [
    [seiteVorne, boxSize.x, boxSize.y, b.grid(v3(0.5, 0.5, 1)), Maths3.quaternion.zero],
    [seiteHinten, boxSize.x, boxSize.y, b.grid(v3(0.5, 0.5, 0)), Maths3.quaternion.y180],
    [seiteLinks, boxSize.z, boxSize.y, b.grid(v3(0, 0.5, 0.5)), Maths3.quaternion.y270],
    [seiteRechts, boxSize.z, boxSize.y, b.grid(v3(1, 0.5, 0.5)), Maths3.quaternion.y90],
    [seiteUnten, boxSize.x, boxSize.z, b.grid(v3(0.5, 0.5, 0.5)), Maths3.quaternion.x90],
  ] as [boolean, number, number, Vector3, Quaternion][];
  const planes = seiten
    .filter(([on]) => on)
    .map(([on, width, height, middlePosition, quaternion]) => {
      const plane = createFakeLightPlane(width, height);
      plane.position.copy(middlePosition);
      plane.quaternion.copy(quaternion).multiply(quat(1, 0, 0, neigung));
      plane.visible = visible;
      return plane;
    });
  return planes;
}
